/**
 * ForgotPassword — Fase B migration to useZodForm (iter-208).
 *
 * Single-field email form with constant-success UI (no email
 * enumeration). After submit, we land in the "sent" state which shows
 * a Resend button governed by a 30 s cooldown. Lang-picker pills
 * allowlisted.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { api } from "../../lib/api";
import { useLang } from "../../contexts/LangContext";
import { LumfiWordmark } from "../../components/LumfiLogo";
import { CheckCircle2, Mail } from "lucide-react";
import { z } from "zod";
import { useZodForm, FormField, SubmitButton } from "../../components/form-ux";

const RESEND_COOLDOWN = 30; // seconds

export default function ForgotPassword() {
  const { lang, setLang } = useLang();
  const isEs = lang === "es";
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [lastEmail, setLastEmail] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const Schema = z.object({
    email: z.string().trim().min(1, isEs ? "El email es obligatorio." : "Email is required.")
      .email(isEs ? "Email inválido." : "Invalid email."),
  });

  const form = useZodForm(Schema, { email: "" }, { formId: "forgot" });

  const onSubmit = form.handleSubmit(async (values) => {
    const cleaned = values.email.trim().toLowerCase();
    try {
      await api.post("/auth/forgot-password", { email: cleaned });
    } catch {
      // Endpoint is constant-response. We still show success on 5xx so
      // attackers can't infer email validity from network behaviour.
    }
    setLastEmail(cleaned);
    setSent(true);
    setCooldown(RESEND_COOLDOWN);
  });

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/auth/forgot-password", { email: lastEmail });
    } catch { /* silent — same as initial submit */ }
    setCooldown(RESEND_COOLDOWN);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10 safe-bottom">
      <div className="w-full max-w-md lumfi-rise-in">
        <div className="text-center mb-7 relative">
          <Link to="/"><LumfiWordmark height={40} /></Link>
          <div className="absolute top-0 right-0 inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm">
            {["es", "en", "pt"].map((code) => (
              // design-system: ALLOW lang-picker toggle pill (3-state segmented control)
              <button /* design-system: ALLOW legacy-todo */
                key={code} type="button" onClick={() => setLang(code)}
                data-testid={`forgot-lang-${code}`}
                className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase transition-colors ${
                  lang === code ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >{code}</button>
            ))}
          </div>
        </div>
        <Card className="rounded-2xl border-slate-200 p-7 shadow-sm">
          {!sent ? (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1 tracking-tight">
                {isEs ? "Recuperar tu clave" : "Recover your password"}
              </h1>
              <p className="text-sm text-slate-500 mb-6">
                {isEs
                  ? "Escribe tu email y te mandamos un link para crear una nueva."
                  : "Enter your email and we'll send you a link to set a new one."}
              </p>
              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <FormField
                  label="Email"
                  name="email"
                  required
                  error={form.errors.email && (form.touched.email || form.submitCount > 0) ? form.errors.email : undefined}
                >
                  <FormField.Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="tú@ejemplo.com"
                    data-testid="forgot-email-input"
                    className="h-12 text-base rounded-xl"
                    {...form.getFieldProps("email")}
                  />
                </FormField>
                <SubmitButton
                  submitting={form.submitting}
                  loadingText={isEs ? "Enviando…" : "Sending…"}
                  testid="forgot-submit-btn"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-base font-semibold"
                >
                  {isEs ? "Enviarme el link" : "Send me the link"}
                </SubmitButton>
              </form>
            </>
          ) : (
            <div data-testid="forgot-success-state">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="h-5 w-5" />
                <h2 className="text-lg font-semibold">
                  {isEs ? "Revisa tu casilla" : "Check your inbox"}
                </h2>
              </div>
              <p className="text-sm text-slate-600 mb-2">
                {isEs ? (
                  <>Si <strong className="text-slate-900">{lastEmail}</strong> tiene una cuenta en Lumfi, te enviamos un link para recuperar tu clave. El link vence en 30 minutos.</>
                ) : (
                  <>If <strong className="text-slate-900">{lastEmail}</strong> has a Lumfi account, we sent you a recovery link. The link expires in 30 minutes.</>
                )}
              </p>
              <p className="text-xs text-slate-500 mb-5">
                {isEs
                  ? "Revisa también la carpeta de spam — los emails de Lumfi vienen de soporte@lumfi.wintimeapp.com."
                  : "Check your spam folder too — Lumfi emails come from soporte@lumfi.wintimeapp.com."}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-10"
                  disabled={cooldown > 0}
                  onClick={resend}
                  data-testid="forgot-resend-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {cooldown > 0
                    ? (isEs ? `Reenviar en ${cooldown}s` : `Resend in ${cooldown}s`)
                    : (isEs ? "Reenviar" : "Resend")}
                </Button>
                <Button asChild className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Link to="/login" data-testid="forgot-back-to-login">
                    {isEs ? "Volver al login" : "Back to login"}
                  </Link>
                </Button>
              </div>
            </div>
          )}
          {!sent && (
            <div className="mt-5 text-sm text-slate-600 text-center">
              {isEs ? "¿Recordaste tu clave?" : "Remembered it?"}{" "}
              <Link to="/login" className="text-emerald-700 font-semibold">
                {isEs ? "Iniciar sesión" : "Sign in"}
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
